import { ESdChangeReason, ICommonSettings } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { Fragment, ReactNode } from "react";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaTabContent from "../../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs, { KuikaTabOption } from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import { ConfigModalNote } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";
import "./config-auth-provider.scss";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const APP_AUTH_OAUTH2_SEARCH_TERMS: string[] = [
  "Access Token URL",
  "Expiration In Minutes",
  "Client ID",
  "Client Secret",
  "Client Authentication",
  "Scope",
  "Access Token",
  "User Name",
  "First Name",
  "Last Name",
  "Refresh Token"
];

const CLIENT_AUTH: KuikaSelectboxOption[] = [
  { value: "true", label: "Send as basic auth header" },
  { value: "false", label: "Send as basic auth body" }
];

const TABS: KuikaTabOption[] = [
  { value: "REQUEST", label: "REQUEST", dataTestId: "oAuth2RequestTab" },
  { value: "RESPONSE", label: "RESPONSE", dataTestId: "oAuth2ResponseTab" }
];
const TAB_KEYS: Record<string, string> = { REQUEST: "request", RESPONSE: "response" };
const TAB_LABELS: Record<string, string> = { request: "REQUEST", response: "RESPONSE" };
/** The panes' keys in the strip's order, so the incoming pane slides in from the side the tab moved to. */
const TAB_ORDER: string[] = TABS.map((tab: KuikaTabOption): string => TAB_KEYS[tab.value]);

export interface IAppConfigAuthOAuth2State {}

export interface IAppConfigAuthOAuth2Props {
  editingId?: string;
  activeKey?: string;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleTabChange: (activeKey: string) => void;
  commonSettings: ICommonSettings;
}

class AppConfigAuthOAuth2 extends PureSdComponent<IAppConfigAuthOAuth2Props & IWithSDProps, IAppConfigAuthOAuth2State> {
  constructor(props: IAppConfigAuthOAuth2Props & IWithSDProps) {
    super(props);
    this.state = {};
  }

  existingNameValidator = async (_rule, value) => {
    const settings = this.props.commonSettings?.oAuth2Settings;

    const isSettingAlreadyExist =
      settings?.some(
        (x) => x.name === value && x.id !== this.props.editingId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Kuika Auth";

    if (isSettingAlreadyExist) throw new Error("oAuth2 Settings Name is already exist.");
  };

  private activeKey = (): string => this.props.activeKey ?? "request";

  render() {
    return (
      <div className="kuika-auth-provider">
        <ConfigField
          label="Name"
          required
          tooltipContentKey="authenticationprovidersettings_oauth2.0_authenticationprovidersettings_name"
        >
          <Form.Item
            name="oAuth2Name"
            rules={[
              { required: true, message: "Please enter a oAuth 2.0 settings name" },
              { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Invalid oAuth 2.0 Settings Name" },
              { validator: this.existingNameValidator }
            ]}
          >
            <KuikaInput size="md" data-testid="oAuth2NameInput" />
          </Form.Item>
        </ConfigField>

        <KuikaTabs
          className="kuika-auth-provider__tabs"
          options={TABS}
          value={TAB_LABELS[this.activeKey()]}
          onChange={(label: string): void => this.props.handleTabChange(TAB_KEYS[label])}
        />

        {/* Both panes stay mounted whichever is showing (`keepMounted`): antd validates and
            submits only the fields that are mounted, and the required User Name is on the pane
            that is not open first. */}
        <KuikaTabContent activeKey={this.activeKey()} order={TAB_ORDER} keepMounted>
          <Fragment key="request">
            <ConfigModalNote>
              {this.props.getSectionDescription(
                "authenticationprovidersettings_oauth2.0_authenticationprovidersettings_request_description"
              )}
            </ConfigModalNote>
            <ConfigField
              label="Access Token URL"
              tooltipContentKey="authenticationprovidersettings_oauth2.0_authenticationprovidersettings_request_accesstokenurl"
            >
              <Form.Item name="accessTokenUrl">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Expiration In Minutes"
              tooltipContentKey="authenticationprovidersettings_oauth2.0_authenticationprovidersettings_request_expirationinminutes"
            >
              <Form.Item name="accessTokenExpirationInMinutes">
                <KuikaInput size="md" type="number" min={0} />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Client ID"
              tooltipContentKey="authenticationprovidersettings_oauth2.0_authenticationprovidersettings_request_clientid"
            >
              <Form.Item name="clientId">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Client Secret"
              tooltipContentKey="authenticationprovidersettings_oauth2.0_authenticationprovidersettings_request_clientsecret"
            >
              <Form.Item name="clientSecret">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Client Authentication"
              tooltipContentKey="authenticationprovidersettings_oauth2.0_authenticationprovidersettings_request_clientauthentication"
            >
              <Form.Item name="clientAuthSendAsBasicAuthHeader">
                <KuikaSelectbox size="md" placeholder="Client Authentication" options={CLIENT_AUTH} />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Scope"
              tooltipContentKey="authenticationprovidersettings_oauth2.0_authenticationprovidersettings_request_scope"
            >
              <Form.Item name="scope">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
          </Fragment>

          <Fragment key="response">
            <ConfigModalNote>
              {this.props.getSectionDescription(
                "authenticationprovidersettings_oauth2.0_authenticationprovidersettings_response_description"
              )}
            </ConfigModalNote>
            <ConfigField
              label="Access Token"
              tooltipContentKey="authenticationprovidersettings_oauth2.0_authenticationprovidersettings_response_accesstoken"
            >
              <Form.Item name="accessTokenKeyName">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="User Name"
              required
              tooltipContentKey="authenticationprovidersettings_oauth2.0_authenticationprovidersettings_response_username"
            >
              <Form.Item
                name="userNameKeyName"
                rules={[{ required: true, message: "Please enter your user name key name!" }]}
              >
                <KuikaInput size="md" data-testid="oAuth2UserNameKeyInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="First Name"
              tooltipContentKey="authenticationprovidersettings_oauth2.0_authenticationprovidersettings_response_firstname"
            >
              <Form.Item name="firstNameKeyName">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Last Name"
              tooltipContentKey="authenticationprovidersettings_oauth2.0_authenticationprovidersettings_response_lastname"
            >
              <Form.Item name="lastNameKeyName">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Refresh Token"
              tooltipContentKey="authenticationprovidersettings_oauth2.0_authenticationprovidersettings_response_refreshtoken"
            >
              <Form.Item name="refreshTokenKeyName">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
          </Fragment>
        </KuikaTabContent>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const appConfigAuthOAuth2 = withSD<IAppConfigAuthOAuth2Props>(AppConfigAuthOAuth2);
export { appConfigAuthOAuth2 as AppConfigAuthOAuth2 };
